import { useContext, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { allElements } from "constants/constants";
import { OeeSummaryDashboardContext } from "contexts/OeeSummaryDashboardContext/OeeSummaryDashboardContext";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { toProductionDateRange } from "utilities/oeeDateRange";
import {
    computeOeeRatios,
    filterRowsByProductionLine,
    groupOeeByProductionLine,
    OeeMetricEnum,
    sumOeeParts,
} from "utilities/oeeSummary";
import OeeByProductionLine from "./components/OeeByProductionLine";
import OeeDowntimeTopFiveByMachineCenter from "./components/OeeDowntimeTopFiveByMachineCenter";
import OeeGaugeCard from "./components/OeeGaugeCard";
import OeeLossByMachineCenter from "./components/OeeLossByMachineCenter";
import OeeSummaryDashboardFilters from "./components/OeeSummaryDashboardFilters";
import OeeTrendByDateRange from "./components/OeeTrendByDateRange";
import useAppliedMachineCentersGuard from "./hooks/useAppliedMachineCentersGuard";
import { useStyles } from "./OeeSummaryDashboard.styles";

const GAUGE_METRICS = [OeeMetricEnum.OEE, OeeMetricEnum.AVAILABILITY, OeeMetricEnum.PERFORMANCE, OeeMetricEnum.QUALITY];

/**
 * OEE Summary report. Both queries run once for the mill, range, machine centers and shift; the
 * clicked production line scopes every card client-side except the per-line card, which always
 * lists every line so another one can be picked.
 */
const OeeSummaryDashboard = () => {
    const classes = useStyles();
    const { millId } = useParams();
    const { dateRange, filters, handleSelectedProductionLineChange, selectedProductionLineName, showFilters } =
        useContext(OeeSummaryDashboardContext);

    // Hosted here, not in the filters panel: it has to run with the panel closed, which is how it opens.
    useAppliedMachineCentersGuard();

    const queryFilters = useMemo(
        () => ({
            endDate: dateRange.endDate,
            machineCenter: filters.machineCenter,
            shift: filters.shift,
            startDate: dateRange.startDate,
        }),
        [dateRange, filters.machineCenter, filters.shift],
    );

    const {
        apolloQueryError: summaryError,
        loading: summaryLoading,
        oeeSummaryRows,
    } = MillsEntity.GetOeeSummaryByMachineCenterDayMillDashboard(queryFilters, Number(millId));
    const { loading: downtimeLoading, oeeDowntimeRows } =
        MillsEntity.GetOeeDowntimeByMachineCenterFailureTypeMillDashboard(queryFilters, Number(millId));

    const selectedProductionLineId = filters.productionLine === allElements ? null : Number(filters.productionLine);
    const productionLineName = selectedProductionLineId === null ? "" : selectedProductionLineName;

    const scopedSummaryRows = useMemo(
        () => filterRowsByProductionLine(oeeSummaryRows, selectedProductionLineId),
        [oeeSummaryRows, selectedProductionLineId],
    );
    const scopedDowntimeRows = useMemo(
        () => filterRowsByProductionLine(oeeDowntimeRows, selectedProductionLineId),
        [oeeDowntimeRows, selectedProductionLineId],
    );
    const ratios = useMemo(() => computeOeeRatios(sumOeeParts(scopedSummaryRows)), [scopedSummaryRows]);
    const productionLines = useMemo(() => groupOeeByProductionLine(oeeSummaryRows), [oeeSummaryRows]);
    const productionDateRange = useMemo(() => toProductionDateRange(dateRange), [dateRange]);

    // A line picked before the range or filters changed can end up without rows; fall back to every line.
    // Only a fetch that succeeded can say so: a failed one also comes back with no rows, and must not throw
    // away the user's selection.
    useEffect(() => {
        if (
            !summaryLoading &&
            !summaryError &&
            selectedProductionLineId !== null &&
            !productionLines.some((line) => line.productionLineId === selectedProductionLineId)
        ) {
            handleSelectedProductionLineChange(allElements, "");
        }
    }, [handleSelectedProductionLineChange, productionLines, selectedProductionLineId, summaryError, summaryLoading]);

    return (
        <>
            {showFilters && <OeeSummaryDashboardFilters />}
            <Box className={classes.gridContainer}>
                <Box className={classes.gaugeColumn}>
                    {GAUGE_METRICS.map((metric) => (
                        <OeeGaugeCard key={metric} loading={summaryLoading} metric={metric} value={ratios[metric]} />
                    ))}
                </Box>
                <OeeByProductionLine lines={productionLines} loading={summaryLoading} />
                <OeeDowntimeTopFiveByMachineCenter
                    loading={downtimeLoading}
                    productionLineName={productionLineName}
                    rows={scopedDowntimeRows}
                />
                <OeeLossByMachineCenter
                    factor={OeeMetricEnum.PERFORMANCE}
                    loading={summaryLoading}
                    productionLineName={productionLineName}
                    rows={scopedSummaryRows}
                />
                <OeeLossByMachineCenter
                    factor={OeeMetricEnum.QUALITY}
                    loading={summaryLoading}
                    productionLineName={productionLineName}
                    rows={scopedSummaryRows}
                />
                <Box className={classes.trendRow}>
                    <OeeTrendByDateRange
                        loading={summaryLoading}
                        productionLineName={productionLineName}
                        range={productionDateRange}
                        rows={scopedSummaryRows}
                    />
                </Box>
            </Box>
        </>
    );
};

export default OeeSummaryDashboard;
